import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Loader2, User, Phone, Mail, Edit, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhotoUpload } from "@/components/PhotoUpload";
import { ParentLinkField } from "@/components/ParentLinkField";
import { StudentCard, ProfileTabs, RoundAvatar } from "@/components/StudentCards";
import { DuplicatesPanel } from "@/components/DuplicatesPanel";
import { PhotoReminderButton } from "@/components/PhotoReminderButton";
import { DeleteAllStudentsButton } from "@/components/DeleteAllStudentsButton";
import { RecycleBinButton } from "@/components/RecycleBinButton";
import { ContactTeacherPicker } from "@/components/ContactTeacherPicker";
import { useSectionsEnabled } from "@/lib/sections";
import { useClassOptions, ClassOptionItems } from "@/components/ClassOptions";

const EMPTY = {
  name: "", photo_url: "", admission_number: "", date_of_birth: "", gender: "Male",
  class_name: "", section: "A", academic_year: "2025-26", admission_date: new Date().toISOString().slice(0, 10),
  father_name: "", mother_name: "", parent_phone: "", parent_email: "", address: "",
  emergency_contact: "", medical_info: "", status: "active", branch_id: "", school_type: "", parent_user_id: "",
};

export default function StudentsPage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [busFilter, setBusFilter] = useState("all");
  const [view, setView] = useState("all");
  const canMerge = ["master_admin", "super_admin", "center_head", "principal"].includes(user?.role);
  const canContact = canMerge || user?.role === "coordinator";
  const multiSec = (() => {
    const m = {};
    students.forEach((s) => { const k = `${s.branch_id}|${s.class_name}`; (m[k] = m[k] || new Set()).add(s.section || "A"); });
    return new Set(Object.keys(m).filter((k) => m[k].size > 1));
  })();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const useSec = useSectionsEnabled();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const isGlobal = user?.role === "master_admin" || user?.role === "super_admin";
  const canCreate = ["master_admin", "super_admin", "principal", "teacher"].includes(user?.role);

  async function loadMeta() {
    const [b, s] = await Promise.all([api.get("/branches"), api.get("/meta/school-types")]);
    setBranches(b.data);
    setSchoolTypes(s.data);
  }

  async function loadStudents() {
    setLoading(true);
    try {
      const params = {};
      if (isGlobal && branchFilter !== "all") params.branch_id = branchFilter;
      if (classFilter !== "all") params.class_name = classFilter;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (busFilter !== "all") params.transport = busFilter;
      if (search) params.search = search;
      const { data } = await api.get("/student-directory", { params });
      setStudents(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMeta().catch(() => {}); }, []);
  useEffect(() => { loadStudents().catch(() => {}); /* eslint-disable-next-line */ }, [search, branchFilter, classFilter, stageFilter, busFilter]);

  function openAdd() {
    const branch = user?.branch_id ? branches.find((b) => b.id === user.branch_id) : branches[0];
    setEditing(null);
    setForm({
      ...EMPTY,
      branch_id: branch?.id || "",
      school_type: branch?.school_type || "",
      class_name: "",
    });
    setOpen(true);
  }

  function openEdit(s) {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      // Pydantic EmailStr rejects "" — send null when the parent email is blank
      const payload = { ...form, parent_email: form.parent_email?.trim() ? form.parent_email.trim() : null };
      if (editing) {
        const patch = { ...payload };
        delete patch.id; delete patch.branch_id; delete patch.school_type; delete patch.admission_number; delete patch.created_at;
        await api.patch(`/students/${editing.id}`, patch);
        toast.success("Student updated");
      } else {
        await api.post("/students", payload);
        toast.success(`${form.name} enrolled successfully`);
      }
      setOpen(false);
      loadStudents();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(s) {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await api.delete(`/students/${s.id}`);
      toast.success("Student deleted");
      loadStudents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const activeBranch = branches.find((b) => b.id === form.branch_id);
  const activeSchoolType = schoolTypes.find((s) => s.id === (activeBranch?.school_type || form.school_type));
  const classOptions = activeSchoolType?.classes || [];

  const canonical = useMemo(() => {
    const branchIds = isGlobal && branchFilter !== "all" ? [branchFilter] : branches.map((b) => b.id);
    const types = new Set(branches.filter((b) => branchIds.includes(b.id)).map((b) => b.school_type));
    return schoolTypes.filter((s) => types.has(s.id)).flatMap((s) => s.classes);
  }, [branches, schoolTypes, branchFilter, isGlobal]);
  const classesForFilter = useClassOptions(isGlobal ? branchFilter : undefined, canonical);

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Student Directory</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Students</h1>
          <p className="text-slate-500 mt-1.5">{students.length} records {isGlobal ? "across all branches" : "at your branch"}</p>
          <div data-testid="student-summary" className="flex flex-nowrap gap-2 mt-3 overflow-x-auto pb-1">
            {[["Playschool", students.filter((s) => s.stage === "playschool").length, "bg-pink-50 text-pink-800 border-pink-100"],
              ["Elementary", students.filter((s) => s.stage === "elementary").length, "bg-violet-50 text-violet-800 border-violet-100"],
              ["On school bus", students.filter((s) => s.uses_transport).length, "bg-amber-50 text-amber-800 border-amber-100"]].map(([l, n, c]) => (
              <div key={l} data-testid={`summary-${l.toLowerCase().replace(/\s+/g, "-")}`} className={`shrink-0 min-w-[110px] rounded-xl border px-3 py-2 ${c}`}>
                <div className="font-display text-xl font-extrabold leading-none">{n}</div>
                <div className="text-[11px] font-medium mt-1 whitespace-nowrap">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
        {canMerge && <PhotoReminderButton branchId={branchFilter} />}
        {canMerge && <DeleteAllStudentsButton students={students} filtered={branchFilter !== "all" || classFilter !== "all" || stageFilter !== "all" || busFilter !== "all" || !!search} onDone={() => loadStudents().catch(() => {})} />}
        {canMerge && <RecycleBinButton onRestored={() => loadStudents().catch(() => {})} />}
        {canCreate && (
          <Button data-testid="add-student-button" onClick={openAdd} className="bg-violet-600 hover:bg-violet-700 h-11 px-5 font-semibold">
            <Plus size={18} className="mr-2" /> Add Student
          </Button>
        )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-violet-100 mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="student-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, admission no. or class"
              className="pl-9 input-focus"
            />
          </div>
          {isGlobal && (
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger data-testid="student-branch-filter" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger data-testid="student-stage-filter" className="w-full sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All school types</SelectItem>
              <SelectItem value="playschool">Playschool (Playgroup–Sr KG)</SelectItem>
              <SelectItem value="elementary">Elementary (Grade 1+)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={busFilter} onValueChange={setBusFilter}>
            <SelectTrigger data-testid="student-transport-filter" className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All students</SelectItem>
              <SelectItem value="yes">Uses school bus</SelectItem>
              <SelectItem value="no">No school bus</SelectItem>
            </SelectContent>
          </Select>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger data-testid="student-class-filter" className="w-full sm:w-44"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              <ClassOptionItems opts={classesForFilter} />
            </SelectContent>
          </Select>
          {canMerge && (
            <Select value={view} onValueChange={setView}>
              <SelectTrigger data-testid="student-dup-filter" className="w-full sm:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="dups">Possible duplicates (merge)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {view === "dups" ? (
        <DuplicatesPanel branchId={branchFilter} onChanged={() => loadStudents().catch(() => {})} />
      ) : loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-violet-500" /></div>
      ) : students.length === 0 ? (
        <Card className="border-dashed border-2 border-violet-200">
          <CardContent className="py-16 text-center">
            <User className="mx-auto text-violet-400" size={44} />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">No students yet</h3>
            <p className="text-slate-500 text-sm mt-1">Enroll your first student to populate the register.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((s) => <StudentCard key={s.id} s={s} onOpen={setProfile} showSection={useSec && multiSec.has(`${s.branch_id}|${s.class_name}`)} />)}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit student" : "Enroll a new student"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-2">
            {!editing && isGlobal && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => {
                    const br = branches.find((b) => b.id === v);
                    setForm({ ...form, branch_id: v, school_type: br?.school_type || "", class_name: "" });
                  }}>
                    <SelectTrigger data-testid="student-form-branch" className="mt-1.5"><SelectValue placeholder="Choose branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>School Type</Label>
                  <Input value={activeSchoolType?.name || ""} disabled className="mt-1.5 bg-slate-50" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Full name" testid="student-form-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <PhotoUpload value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TextField label="Admission No." testid="student-form-admission" value={form.admission_number} onChange={(v) => setForm({ ...form, admission_number: v })} required disabled={!!editing} />
              <TextField label="Date of birth" type="date" testid="student-form-dob" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} required />
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="student-form-gender" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Class</Label>
                <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })} disabled={classOptions.length === 0}>
                  <SelectTrigger data-testid="student-form-class" className="mt-1.5"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {classOptions.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {useSec && <TextField label="Section (optional)" testid="student-form-section" value={form.section} onChange={(v) => setForm({ ...form, section: v })} />}
              <TextField label="Academic year" testid="student-form-year" value={form.academic_year} onChange={(v) => setForm({ ...form, academic_year: v })} />
            </div>
            <TextField label="Admission date" type="date" testid="student-form-admission-date" value={form.admission_date} onChange={(v) => setForm({ ...form, admission_date: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Father's name" testid="student-form-father" value={form.father_name} onChange={(v) => setForm({ ...form, father_name: v })} required />
              <TextField label="Mother's name" testid="student-form-mother" value={form.mother_name} onChange={(v) => setForm({ ...form, mother_name: v })} required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Parent phone" testid="student-form-phone" value={form.parent_phone} onChange={(v) => setForm({ ...form, parent_phone: v })} required />
              <TextField label="Parent email" testid="student-form-email" value={form.parent_email} onChange={(v) => setForm({ ...form, parent_email: v })} type="email" />
            </div>
            <ParentLinkField value={form.parent_user_id} branchId={form.branch_id} onChange={(v) => setForm({ ...form, parent_user_id: v })} />
            <div>
              <Label>Address</Label>
              <Textarea data-testid="student-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Emergency contact" testid="student-form-emergency" value={form.emergency_contact} onChange={(v) => setForm({ ...form, emergency_contact: v })} required />
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="student-form-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="alumni">Alumni</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Medical info</Label>
              <Textarea data-testid="student-form-medical" value={form.medical_info} onChange={(e) => setForm({ ...form, medical_info: e.target.value })} className="mt-1.5" placeholder="Allergies, conditions, notes..." />
            </div>

            <Button data-testid="save-student-submit" type="submit" disabled={saving} className="w-full h-11 bg-violet-600 hover:bg-violet-700">
              {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? "Save changes" : "Enroll student"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile dialog */}
      <Dialog open={!!profile} onOpenChange={(v) => !v && setProfile(null)}>
        <DialogContent className="max-w-xl">
          {profile && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Student profile</DialogTitle>
              </DialogHeader>
              <div className="pt-2">
                <div className="flex items-center gap-4">
                  <RoundAvatar name={profile.name} photo={profile.photo_url} size="h-16 w-16 text-xl" />
                  <div>
                    <div className="font-display text-xl font-bold text-slate-900">{profile.name}</div>
                    <div className="text-sm text-slate-500 font-mono">{profile.admission_number}</div>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap">
                      <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">{profile.class_name}</Badge>
                      {useSec && multiSec.has(`${profile.branch_id}|${profile.class_name}`) && <Badge variant="outline">Section {profile.section}</Badge>}
                      <Badge variant="outline" className="capitalize">{profile.status}</Badge>
                      {profile.uses_transport && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">School bus</Badge>}
                    </div>
                  </div>
                </div>
                <ContactTeacherPicker student={profile} canEdit={canContact}
                  onChange={(u) => { setProfile({ ...profile, ...u }); setStudents((xs) => xs.map((x) => (x.id === profile.id ? { ...x, ...u } : x))); }} />
                <ProfileTabs student={profile} details={
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-3 text-sm">
                  <Info label="Gender" value={profile.gender} />
                  <Info label="Date of birth" value={profile.date_of_birth} />
                  <Info label="Admission date" value={profile.admission_date} />
                  <Info label="Academic year" value={profile.academic_year} />
                  <Info label="Father" value={profile.father_name} />
                  <Info label="Mother" value={profile.mother_name} />
                  <Info label="Parent phone" value={profile.parent_phone} icon={Phone} />
                  <Info label="Parent email" value={profile.parent_email || "—"} icon={Mail} />
                  <Info label="Emergency" value={profile.emergency_contact} />
                  <Info label="Address" value={profile.address} />
                  {profile.admission_status && <Info label="Application status" value={profile.admission_status} />}
                  {profile.indent_id && <Info label="Indent ID" value={profile.indent_id} />}
                  {profile.uniform_size && <Info label="Uniform size" value={profile.uniform_size} />}
                  {profile.kit_taken != null && <Info label="Kit taken" value={profile.kit_taken ? "Yes" : "No"} />}
                  {profile.sr_no && <Info label="Sr No" value={profile.sr_no} />}
                  {profile.medical_info && <Info label="Medical info" value={profile.medical_info} full />}
                </div>} />
                {canCreate && (
                  <div className="flex gap-2 mt-6 pt-5 border-t border-slate-100">
                    <Button data-testid="profile-edit-btn" onClick={() => { setProfile(null); openEdit(profile); }} className="flex-1 bg-violet-600 hover:bg-violet-700">
                      <Edit size={16} className="mr-2" /> Edit
                    </Button>
                    {["master_admin", "super_admin", "principal"].includes(user?.role) && (
                      <Button data-testid="profile-delete-btn" variant="outline" onClick={() => { remove(profile); setProfile(null); }} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                        <Trash2 size={16} className="mr-2" /> Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function TextField({ label, value, onChange, testid, type = "text", required, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 input-focus"
        required={required}
        disabled={disabled}
      />
    </div>
  );
}

function Info({ label, value, icon: Icon, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-800 flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-slate-400" />}
        {value}
      </div>
    </div>
  );
}
